import type { TaskAnimalV1, TaskAssignmentReason, TaskAggregateAssignmentReason } from '@/model/Task.model';

const abbreviateDay = (day: string): string => {
  const abbreviations: Record<string, string> = {
    MONDAY: 'Mon',
    TUESDAY: 'Tue',
    WEDNESDAY: 'Wed',
    THURSDAY: 'Thu',
    FRIDAY: 'Fri',
    SATURDAY: 'Sat',
    SUNDAY: 'Sun',
  };
  return abbreviations[day] || day;
};

/**
 * Formats a sorted array of age values into a summary string with truncation
 * for lists longer than 3 values (shows first 2 then count of remaining).
 */
const formatAgeValues = (sorted: Array<number>): string => {
  const n = sorted.length;
  if (n === 1) {
    return `Age: ${sorted[0]} ${sorted[0] === 1 ? 'Day' : 'Days'}`;
  }
  if (n === 2) {
    return `Age: ${sorted[0]} & ${sorted[1]} Days`;
  }
  if (n === 3) {
    return `Age: ${sorted[0]}, ${sorted[1]} & ${sorted[2]} Days`;
  }
  const remaining = n - 2;
  return `Age: ${sorted[0]}, ${sorted[1]} & ${remaining} Other Days`;
};

/**
 * Formats a sorted array of day values with a label prefix, using truncation
 * for lists longer than 3 values (shows first 2 then count of remaining).
 */
const formatDayValues = (label: string, sorted: Array<number>): string => {
  const n = sorted.length;
  if (n === 1) {
    return `${label}: Day ${sorted[0]}`;
  }
  if (n === 2) {
    return `${label}: Days ${sorted[0]} & ${sorted[1]}`;
  }
  if (n === 3) {
    return `${label}: Days ${sorted[0]}, ${sorted[1]} & ${sorted[2]}`;
  }
  const remaining = n - 2;
  return `${label}: Days ${sorted[0]}, ${sorted[1]} & ${remaining} Others`;
};

/**
 * Formats a task-level aggregate assignment reason into a human-readable string.
 * Returns null for one_off and unknown types (should not be displayed).
 */
export const formatAggregateAssignmentReason = (reason: TaskAggregateAssignmentReason): string | null => {
  switch (reason.type) {
    case 'age':
      return formatAgeValues([...reason.values].sort((a, b) => a - b));
    case 'study_day':
      return formatDayValues(
        'Study',
        [...reason.values].sort((a, b) => a - b)
      );
    case 'tracking_day':
      return formatDayValues(
        'Tracking',
        [...reason.values].sort((a, b) => a - b)
      );
    case 'recurring_daily':
      if (reason.occurrence != null && reason.total != null) {
        return `Recurrence: Day ${reason.occurrence} of ${reason.total}`;
      }
      return 'Recurrence: Daily';
    case 'recurring_weekly':
      if (reason.occurrence != null && reason.total != null) {
        return `Recurrence: Week ${reason.occurrence} of ${reason.total} (${abbreviateDay(reason.day_of_week)})`;
      }
      return `Recurrence: Weekly (${abbreviateDay(reason.day_of_week)})`;
    case 'one_off':
      return null;
    case 'unknown':
      return null;
  }
};

/**
 * Formats a per-animal assignment reason into a human-readable string.
 * Delegates to formatAggregateAssignmentReason by wrapping scalar values.
 */
export const formatAssignmentReason = (reason: TaskAssignmentReason): string => {
  const aggregate = toAggregate(reason);
  return formatAggregateAssignmentReason(aggregate) ?? '';
};

const toAggregate = (reason: TaskAssignmentReason): TaskAggregateAssignmentReason => {
  switch (reason.type) {
    case 'age':
      return { type: 'age', values: [reason.value] };
    case 'study_day':
      return { type: 'study_day', values: [reason.value] };
    case 'tracking_day':
      return { type: 'tracking_day', values: [reason.value] };
    case 'recurring_daily':
      return { type: 'recurring_daily', occurrence: reason.occurrence, total: reason.total };
    case 'recurring_weekly':
      return {
        type: 'recurring_weekly',
        day_of_week: reason.day_of_week,
        occurrence: reason.occurrence,
        total: reason.total,
      };
    case 'one_off':
      return { type: 'one_off' };
    case 'unknown':
      return { type: 'unknown' };
  }
};

/**
 * Summarizes task assignment reasons across all animals into a concise single-line overview.
 * Ignores 'unknown' and 'one_off' types. Returns empty string if no valid reasons exist.
 */
export const summarizeAssignmentReasons = (animals: Array<TaskAnimalV1>): string => {
  const ageValues = new Set<number>();
  const studyDayValues = new Set<number>();
  const trackingDayValues = new Set<number>();
  let recurringDailyReason: TaskAssignmentReason | null = null;
  let recurringWeeklyReason: TaskAssignmentReason | null = null;

  for (const animal of animals) {
    const reason = animal.assignment_reason;
    switch (reason.type) {
      case 'age':
        ageValues.add(reason.value);
        break;
      case 'study_day':
        studyDayValues.add(reason.value);
        break;
      case 'tracking_day':
        trackingDayValues.add(reason.value);
        break;
      case 'recurring_daily':
        recurringDailyReason = reason;
        break;
      case 'recurring_weekly':
        recurringWeeklyReason = reason;
        break;
      case 'one_off':
      case 'unknown':
        break;
    }
  }

  if (ageValues.size > 0) {
    return formatAggregateAssignmentReason({ type: 'age', values: Array.from(ageValues) }) ?? '';
  }

  if (studyDayValues.size > 0) {
    return formatAggregateAssignmentReason({ type: 'study_day', values: Array.from(studyDayValues) }) ?? '';
  }

  if (trackingDayValues.size > 0) {
    return formatAggregateAssignmentReason({ type: 'tracking_day', values: Array.from(trackingDayValues) }) ?? '';
  }

  if (recurringDailyReason !== null) {
    return formatAssignmentReason(recurringDailyReason);
  }

  if (recurringWeeklyReason !== null) {
    return formatAssignmentReason(recurringWeeklyReason);
  }

  return '';
};
